<template>
  <div>
    <div v-if="!noJobFound" class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div class="sm:mx-auto sm:w-full sm:max-w-full">
        <ApplyNew :record="job" :team="team" />
      </div>
    </div>
    <div v-else>
      <main
        class="h-screen"
        style="
          background-image: url('https://images.unsplash.com/photo-1545972154-9bb223aac798?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=3050&q=80&exp=8&con=-15&sat=-75');
        "
      >
        <div class="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 sm:py-24 lg:px-8 lg:py-48">
          <p class="text-base font-semibold text-black text-opacity-50">404</p>
          <h1 class="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">Uh oh! I think you're lost.</h1>
          <p class="mt-2 text-lg font-medium text-black text-opacity-50">
            It looks like the job you're looking for doesn't exist. It may have expired or been removed. Check out some
            of our other jobs below!
          </p>
          <div class="mt-6">
            <a
              href="https://www.humaniz.io/careers"
              class="inline-flex items-center rounded-md border border-transparent bg-white bg-opacity-75 px-4 py-2 text-sm font-medium text-black text-opacity-75 sm:bg-opacity-25 sm:hover:bg-opacity-50"
              >Find Other Jobs</a
            >
          </div>
        </div>
      </main>
    </div>
    <Alert ref="alert" />
  </div>
</template>

<script>
import Alert from '../shared/Alert.vue';
import ApplyNew from './ApplyNew.vue';

export default {
  components: {
    Alert,
    ApplyNew,
  },
  data() {
    return {
      selectedTab: 'Apply',
      team: {
        companyLogo: '',
        company: '',
        zipId: '',
      },
      job: {
        id: '',
        title: '',
        description: '',
        city: '',
        state: '',
        createdAt: '',
        qualifyingFilter: '',
        jobType: '',
        resumeRequired: false,
      },
      noJobFound: false,
      jsonLdScript: null,
    };
  },
  mounted() {
    this.getJob();
  },
  updated() {
    // Update JSON-LD when component data changes
    if (this.job.id && !this.noJobFound) {
      this.updateJsonLd();
    }
  },
  beforeUnmount() {
    // Clean up by removing the script tag when component is destroyed
    this.removeJsonLd();
  },
  methods: {
    handleSelectedTab(e) {
      this.selectedTab = e;
    },
    async getJob() {
      this.$store.commit('general/showLoadingSpinner');
      try {
        const response = await fetch(`/public/apply/${this.$route.params.id}`);
        this.$store.commit('general/hideLoadingSpinner');
        const data = await response.json();
        
        if (data.record) {
          this.job.id = data.record.indeed_ref_id;
          this.job.title = data.record.title;
          this.job.description = data.record.description;
          this.job.city = data.record.job_locations.find(({ id }) => id === data.job_location_id).city;
          this.job.state = data.record.job_locations.find(({ id }) => id === data.job_location_id).state;
          this.job.country = data.record.job_locations.find(({ id }) => id === data.job_location_id).country;
          this.job.createdAt = data.record.created_at;
          this.team.companyLogo = data.team.company_logo;
          this.team.company = data.team.company;
          this.job.qualifyingFilter = data.record.real_estate_qualifying_filter || data.record.qualifying_filter;
          this.team.zipId = data.team.zip_recruiter_conversion_tracking_id;
          this.job.jobType = data.record.job_type;
          this.job.resumeRequired = data.record.resume_required;

          // Add JSON-LD after job data is loaded
          this.updateJsonLd();
        } else {
          this.noJobFound = true;
        }
      } catch (error) {
        this.$store.commit('general/hideLoadingSpinner');
        this.noJobFound = true;
        console.error('Error fetching job:', error);
      }
    },
    getJsonLdData() {
      return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: this.job.title,
        description: this.job.description,
        directApply: 'true',
        identifier: {
          '@type': 'PropertyValue',
          name: 'Google',
          value: this.job.id,
        },
        datePosted: this.job.createdAt,
        validThrough: this.job.createdAt,
        employmentType: 'FULL_TIME',
        hiringOrganization: {
          '@type': 'Organization',
          name: this.team.company,
          logo: this.team.companyLogo,
        },
        jobLocation: [
          {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              addressLocality: this.job.city,
              addressRegion: this.job.state,
              addressCountry: this.job.country,
            },
          },
        ],
      };
    },
    updateJsonLd() {
      // Remove existing script if it exists
      this.removeJsonLd();

      // Create new script element
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(this.getJsonLdData());
      document.head.appendChild(script);

      // Store reference to script for later removal
      this.jsonLdScript = script;
    },
    removeJsonLd() {
      if (this.jsonLdScript && document.head.contains(this.jsonLdScript)) {
        document.head.removeChild(this.jsonLdScript);
        this.jsonLdScript = null;
      }
    },
  },
};
</script>
